import React from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  HeartPulse,
  Factory,
  Landmark,
  ShoppingBag,
  GraduationCap,
  ArrowRight,
} from 'lucide-react';
import './Industries.css';

const industries = [
  {
    id: 1,
    icon: Building2,
    name: 'Financial Services',
    description:
      'We deliver rock-solid digital banking, fraud-detection, and compliance platforms tailored for modern financial institutions. Our solutions streamline operations while maintaining the highest regulatory standards.',
    stat: '40% cost reduction',
    accentColor: '#06b6d4',
  },
  {
    id: 2,
    icon: HeartPulse,
    name: 'Healthcare',
    description:
      'From EMR integrations to AI-assisted diagnostics, we power the next generation of patient-centric healthcare technology. Our HIPAA-compliant systems ensure data security at every touchpoint.',
    stat: '60% faster diagnostics',
    accentColor: '#ef4444',
  },
  {
    id: 3,
    icon: Factory,
    name: 'Manufacturing',
    description:
      'Smart factory automation, predictive maintenance, and real-time supply-chain visibility built for Industry 4.0. We bridge OT and IT to unlock operational excellence at scale.',
    stat: '35% efficiency gain',
    accentColor: '#f59e0b',
  },
  {
    id: 4,
    icon: Landmark,
    name: 'Government',
    description:
      'Secure e-governance portals, citizen-service platforms, and data infrastructure that meet the strictest sovereign compliance requirements. We help public institutions modernise without compromise.',
    stat: '3× faster service delivery',
    accentColor: '#7c3aed',
  },
  {
    id: 5,
    icon: ShoppingBag,
    name: 'Retail & E-Commerce',
    description:
      'Omni-channel commerce engines, personalisation AI, and warehouse automation that turn browsers into loyal buyers. Our platforms scale seamlessly from startup to enterprise.',
    stat: '50% revenue uplift',
    accentColor: '#10b981',
  },
  {
    id: 6,
    icon: GraduationCap,
    name: 'Education',
    description:
      'Interactive LMS platforms, adaptive learning algorithms, and secure student-data ecosystems redefining modern education. We empower institutions to deliver world-class learning experiences.',
    stat: '80% engagement increase',
    accentColor: '#2563eb',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Industries() {
  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Industries Served by Wayug Infotech',
          itemListElement: industries.map((ind, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: ind.name,
          })),
        })}
      </script>

      <section id="industries" className="industries">
        <div className="container">
          {/* ── Section Header ── */}
          <div className="section-header">
            <span className="section-label">INDUSTRIES WE SERVE</span>
            <h2 className="section-title">
              Trusted Across{' '}
              <span className="gradient-text">Every Sector</span>
            </h2>
            <p className="section-subtitle">
              From regulated financial markets to high-volume e-commerce, Wayug
              Infotech delivers precision-engineered technology solutions that
              meet the unique demands of every industry.
            </p>
          </div>

          {/* ── Bento Grid ── */}
          <motion.div
            className="industries-bento"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {industries.map((industry) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={industry.id}
                  className="industry-card"
                  variants={cardVariants}
                  whileHover={{ scale: 1.02 }}
                  style={{ '--accent': industry.accentColor }}
                >
                  {/* Radial glow overlay — driven by CSS via --accent */}
                  <span className="industry-card__glow" aria-hidden="true" />

                  <div
                    className="industry-icon-wrap"
                    style={{
                      background: `${industry.accentColor}18`,
                      border: `1px solid ${industry.accentColor}30`,
                    }}
                  >
                    <Icon size={22} color={industry.accentColor} strokeWidth={1.75} />
                  </div>

                  <h3 className="industry-name">{industry.name}</h3>
                  <p className="industry-desc">{industry.description}</p>

                  <span
                    className="industry-stat"
                    style={{
                      color: industry.accentColor,
                      background: `${industry.accentColor}14`,
                    }}
                  >
                    {industry.stat}
                  </span>

                  <span className="industry-arrow" aria-hidden="true">
                    <ArrowRight size={18} />
                  </span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>
    </>
  );
}